// #region imports
import { useEffect, useState, RefObject, useRef } from 'react';
import useDraggable from '../../core/hooks/useDraggable';
import { WindowSizes } from '../../windows/constants/windowSizes';
import { SheetDefaults } from '../constants/sheetDefaults';
import { ReturnTypeofSheetSectionsSelector } from '../selectors/sheetUiSelector';
import { getFreezeBarHandleSize } from '../utils/viewport/viewportUtils';
import _, { debounce } from 'lodash';
import { FilterResults, Freeze, GridEntity, IRange } from '../types';
import {
  getUnHiddenPosition,
  getViewportStartEnd,
} from '../utils/views/virtualViewport';
import {
  UseScrollbar,
  calculateThumbSize,
  getMaxAvailableCountForScroll,
  mapScrolledIndexToVisibleIndex,
} from './useScrollbar';
import { UseSheetTheme } from './useSheetTheme';
import { setViewportFromScrollbarPosition } from '../redux/sagas/views/viewsActions';
import { useAppDispatch } from '../../core/hooks/storeHooks';
import { DataDirection } from '../../core/types/ui/position';
import { sortRanges } from '../utils/area/range';
// #endregion

export interface RowScrollbarProps
  extends ReturnTypeofSheetSectionsSelector,
    UseSheetTheme,
    Omit<UseScrollbar, 'handleWheelScroll'> {
  sheetId: string;
  gridHeight: number;
  windowWidth: number;
  windowHeight: number;
}

export default (props: RowScrollbarProps) => {
  // #region states
  const dispatch = useAppDispatch();
  const thumbRef = useRef<HTMLDivElement>(null);

  const [isFreed, setIsFreed] = useState(true);
  const [thumbPosition, setThumbPosition] = useState(0);
  const [thumbHeight, setThumbHeight] = useState(60);

  const scrollGeometry = getScrollbarGeometry(props, thumbHeight);
  // #endregion

  // #region variables
  const {
    sheetId,
    windowHeight,
    windowWidth,
    rowViewport,
    freezedRows,
    maxRowIndex,
    filterResults,
  } = props;

  // Sorting is important to map virtualIndex to actualIndex
  const sortedHiddenRows = sortRanges(props.hiddenRows);

  const firstNonFrozenRowIndex = getUnHiddenPosition(
    [], // We don't need rowSizes for this
    [], //  We don't need to manage hidden for scrollbar as it is managed in map function
    0,
    maxRowIndex,
    DataDirection.Forward,
    freezedRows,
    filterResults,
    0,
    { useNonFreezeMin: true }
  );

  const maxIndex = getMaxRowIndexForScroll(
    firstNonFrozenRowIndex,
    freezedRows,
    sortedHiddenRows,
    filterResults,
    maxRowIndex
  );
  // #endregion

  // #region useEffects
  useEffect(() => {
    if (isFreed) {
      const { visibleThumbMax } = scrollGeometry;
      const { start, end } = getViewportStartEnd(rowViewport.nonFrozenIndexes);

      const virtualIndex = getMaxAvailableCountForScroll(
        start,
        sortedHiddenRows,
        firstNonFrozenRowIndex
      );

      const thumbPosition =
        end >= maxRowIndex
          ? visibleThumbMax
          : (virtualIndex * visibleThumbMax) / maxIndex;

      setThumbPosition(thumbPosition);
      requestAnimationFrame(() =>
        updateThumbPositionThroughRef(thumbRef, thumbPosition)
      );
    }
  }, [rowViewport, isFreed, scrollGeometry.visibleThumbMax]);

  useEffect(() => {
    const minHeight = 20;
    const maxHeight = 200;

    if (rowViewport.nonFrozenIndexes.length - 1 === maxIndex) {
      setThumbHeight(0);
    } else {
      const thumbSize = calculateThumbSize(
        scrollGeometry.scrollAreaHeight,
        minHeight,
        maxIndex,
        maxHeight
      );
      setThumbHeight(Math.max(thumbSize, minHeight));
    }
  }, [scrollGeometry.scrollAreaHeight, maxIndex]);
  // #endregion

  // #region thumb drag
  const dispatchChanges = debounce((virtualIndex) => {
    dispatch(
      setViewportFromScrollbarPosition({
        sheetId: sheetId,
        entityType: GridEntity.Row,
        expectedIndex: virtualIndex,
        windowHeight: windowHeight,
        windowWidth: windowWidth,
      })
    );
  }, 10);

  const onThumbDrag = (
    x: number,
    y: number,
    e: MouseEvent,
    dragEnd?: boolean
  ) => {
    const { scrollThumbMinPos, scrollThumbMaxPos, visibleThumbMax } =
      scrollGeometry;

    const newPos = _.clamp(y, scrollThumbMinPos, scrollThumbMaxPos);
    const visiblePos = newPos - scrollGeometry.scrollThumbMinPos;

    const virtualIndex = Math.round((maxIndex * visiblePos) / visibleThumbMax);

    const visibleIndex = convertVirtualIndextoActualIndex(
      virtualIndex,
      firstNonFrozenRowIndex,
      freezedRows,
      sortedHiddenRows,
      filterResults
    );

    requestAnimationFrame(() =>
      updateThumbPositionThroughRef(thumbRef, visiblePos)
    );

    dispatchChanges(visibleIndex);

    if (dragEnd) {
      setTimeout(() => {
        setIsFreed(true);
      }, 13);
      setThumbPosition(visiblePos);
    }
  };

  const { onMouseDown } = useDraggable({
    x: 0,
    y: scrollGeometry.scrollThumbMinPos + thumbPosition,
    onDrag: onThumbDrag,
  });

  const onThumbMouseDown = (e: React.MouseEvent) => {
    setIsFreed(false);
    onMouseDown(e);
  };
  // #endregion

  return {
    ...scrollGeometry,
    scrollThumbHeight: thumbHeight,
    thumbPosition,

    thumbRef,
    onThumbMouseDown,
  };
};

// #region helpers
const getScrollbarGeometry = (
  props: RowScrollbarProps,
  scrollThumbHeight: number
) => {
  const { gridHeight, freezedRows, freezedRowsSum } = props;

  const scrollbarWidth = SheetDefaults.scrollBarWidth;
  const buttonSize = scrollbarWidth;
  const scrollBarHeight = gridHeight + SheetDefaults.scrollBarHeight;
  const topSectionHeight = Math.max(
    props.colHeaderHeight + props.colFilterHeight,
    buttonSize * 2
  );
  const bottomSectionHeight = buttonSize * 2;

  const freezeBarHandleSize = getFreezeBarHandleSize(freezedRows, props.zoom);

  const frozenAreaHeight = freezedRows.frozen
    ? freezedRowsSum + freezeBarHandleSize
    : 0;

  const scrollAreaHeight = Math.max(
    scrollBarHeight - topSectionHeight - bottomSectionHeight - frozenAreaHeight,
    0
  );

  const scrollThumbMinPos =
    WindowSizes.controlBarHeight +
    props.formulaBarHeight +
    props.groupBarHeight +
    topSectionHeight +
    frozenAreaHeight;

  const scrollThumbMaxPos =
    scrollThumbMinPos + scrollAreaHeight - scrollThumbHeight;

  const visibleThumbMax = scrollThumbMaxPos - scrollThumbMinPos; // without window y and other top sections

  return {
    scrollbarWidth,
    buttonSize,
    scrollBarHeight,
    topSectionHeight,
    bottomSectionHeight,
    frozenAreaHeight,
    scrollAreaHeight,

    scrollThumbMinPos,
    scrollThumbMaxPos,

    visibleThumbMax,
  };
};

export const updateThumbPositionThroughRef = (
  thumbRef: RefObject<HTMLDivElement>,
  visiblePos: number
) => {
  if (thumbRef.current) {
    thumbRef.current.style.top = `${visiblePos}px`;
  }
};

// Max Rows are available number of rows to scroll subtracting freeze and non filtered rows
const getMaxRowIndexForScroll = (
  firstNonFrozenRowIndex: number,
  freezedRows: Freeze,
  sortedHiddenRows: IRange[],
  filterResults: FilterResults,
  maxRowIndex: number
): number => {
  if (filterResults.hasFilter) {
    const index = filterResults.filterIndexesMap[firstNonFrozenRowIndex]; // Get the index of the first non-frozen row in the filtered results.

    const afterFreeze = freezedRows.frozen ? freezedRows.end + 1 : -1;
    const minLimit = Math.max(afterFreeze, filterResults.lastRow + 1);

    const rowsAfterLastFilteredRow = getMaxAvailableCountForScroll(
      maxRowIndex,
      sortedHiddenRows,
      minLimit
    );
    if (index === undefined) {
      return rowsAfterLastFilteredRow;
    } else {
      const rowsInFilter = filterResults.rowsIndexes.length - index;
      return rowsInFilter + rowsAfterLastFilteredRow;
    }
  }

  const minIndex = freezedRows.frozen ? freezedRows.end + 1 : 0;
  return getMaxAvailableCountForScroll(maxRowIndex, sortedHiddenRows, minIndex);
};

const convertVirtualIndextoActualIndex = (
  virtualIndex: number,
  firstNonFrozenRowIndex: number,
  freezedRows: Freeze,
  sortedHiddenRows: IRange[],
  filterResults: FilterResults
) => {
  if (filterResults.hasFilter) {
    const index = filterResults.filterIndexesMap[firstNonFrozenRowIndex]; // Get the index of the first non-frozen row in the filtered results.
    const expIndex = index + virtualIndex; // Calculate the expected index in the filtered results based on the virtual index(always starts with 0).

    // Check if the expected index exists in the filtered rows.
    const afterFreeze = freezedRows.frozen ? freezedRows.end + 1 : -1;
    const afterFilteredRow = filterResults.lastRow + 1;
    const indexInFilter = filterResults.rowsIndexes[expIndex];

    const nonFrozenMax = Math.max(afterFreeze, afterFilteredRow);

    if (indexInFilter !== undefined) {
      // If the index exists in the filtered rows, return it as the actual index.
      return indexInFilter;
    } else {
      let scrolledIndex = expIndex;

      if (isNaN(expIndex)) {
        // If the calculated expected index is not a number (NaN), set scroll index to virtualIndex
        scrolledIndex = virtualIndex;
      } else {
        // Calculate the adjusted index by subtracting the length of filtered rows from the expected index.
        const adjustedIndex = expIndex - filterResults.rowsIndexes.length;
        scrolledIndex = adjustedIndex;
      }

      return mapScrolledIndexToVisibleIndex(
        scrolledIndex,
        sortedHiddenRows,
        nonFrozenMax
      );
    }
  }

  const minIndex = freezedRows.frozen ? freezedRows.end + 1 : 0;
  return mapScrolledIndexToVisibleIndex(
    virtualIndex,
    sortedHiddenRows,
    minIndex
  );
};
// #endregion
